import React from 'react';
import { motion } from 'framer-motion';
import { SERVICES } from '../config/assets';
import { ServiceVideoCard } from '../components/ServiceVideoCard';
import { Check, ArrowRight } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';

const ServicesPage = () => {
  const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, amount: 0.3 },
    transition: { duration: 0.35 }
  };

  // Get service-specific styling for hover effects
  const getServiceHoverStyle = (serviceSlug) => {
    switch (serviceSlug) {
      case 'polished-concrete':
        return 'hover:border-amber-300 dark:hover:border-amber-700 hover:shadow-amber-100 dark:hover:shadow-amber-950 hover:bg-gradient-to-br hover:from-amber-50 hover:to-orange-50 dark:hover:from-amber-950 dark:hover:to-orange-950';
      case 'grind-and-seal':
        return 'hover:border-blue-300 dark:hover:border-blue-700 hover:shadow-blue-100 dark:hover:shadow-blue-950 hover:bg-gradient-to-br hover:from-blue-50 hover:to-cyan-50 dark:hover:from-blue-950 dark:hover:to-cyan-950';
      case 'honed-concrete':
        return 'hover:border-emerald-300 dark:hover:border-emerald-700 hover:shadow-emerald-100 dark:hover:shadow-emerald-950 hover:bg-gradient-to-br hover:from-emerald-50 hover:to-teal-50 dark:hover:from-emerald-950 dark:hover:to-teal-950';
      case 'concrete-grinding-floor-prep':
        return 'hover:border-orange-300 dark:hover:border-orange-700 hover:shadow-orange-100 dark:hover:shadow-orange-950 hover:bg-gradient-to-br hover:from-orange-50 hover:to-red-50 dark:hover:from-orange-950 dark:hover:to-red-950';
      default:
        return 'hover:border-primary hover:shadow-lg';
    }
  };

  // Get service-specific button styling
  const getServiceButtonStyle = (serviceSlug) => {
    switch (serviceSlug) {
      case 'polished-concrete':
        return 'bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white border-0';
      case 'grind-and-seal':
        return 'bg-gradient-to-r from-blue-500 to-cyan-600 hover:from-blue-600 hover:to-cyan-700 text-white border-0';
      case 'honed-concrete':
        return 'bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white border-0';
      case 'concrete-grinding-floor-prep':
        return 'bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-white border-0';
      default:
        return 'btn-primary';
    }
  };

  return (
    <div className="min-h-screen pt-16" data-testid="services-page">
      {/* Hero */}
      <section className="section-spacing bg-[radial-gradient(1200px_600px_at_50%_50%,#EEEDEC_0%,#F7F7F6_45%,#FAFAF9_100%)]">
        <div className="container-custom text-center">
          <motion.div {...fadeInUp}>
            <h1 className="text-4xl md:text-5xl font-bold mb-6">Our Services</h1>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
              Professional concrete polishing and preparation services for commercial and residential projects across Sydney
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="section-spacing bg-background">
        <div className="container-custom">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-16" data-testid="services-grid">
            {SERVICES.map((service, index) => (
              <motion.div
                key={service.slug}
                {...fadeInUp}
                transition={{ duration: 0.35, delay: index * 0.1 }}
              >
                <ServiceVideoCard
                  video={service.video}
                  title={service.name}
                  description={service.description}
                  testid={`service-detail-card-${service.slug}`}
                />
              </motion.div>
            ))}
          </div>

          {/* Detailed Service Descriptions */}
          <div className="space-y-12">
            {SERVICES.map((service, index) => (
              <motion.div key={service.slug} {...fadeInUp} transition={{ delay: 0.1 * index }}>
                <Card 
                  className={`overflow-hidden cursor-pointer transition-all duration-300 hover:shadow-lg ${getServiceHoverStyle(service.slug)}`}
                  data-testid={`service-detail-${service.slug}`}
                  onClick={() => window.location.href = `/services/${service.slug}`}
                >
                  <CardHeader>
                    <div className="flex justify-between items-start">
                      <CardTitle className="text-2xl">{service.name}</CardTitle>
                      <div className="text-right">
                        <div className="text-sm text-muted-foreground mb-2">Click to learn more</div>
                        <ArrowRight className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors ml-auto" />
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground leading-relaxed mb-6">
                      {service.longDescription}
                    </p>
                    <div className="grid md:grid-cols-2 gap-6 mb-6">
                      <div>
                        <h4 className="font-semibold mb-3">Benefits:</h4>
                        <ul className="space-y-2">
                          <li className="flex items-start gap-2">
                            <Check className="h-5 w-5 text-primary mt-0.5" />
                            <span className="text-sm text-muted-foreground">Highly durable and long-lasting</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <Check className="h-5 w-5 text-primary mt-0.5" />
                            <span className="text-sm text-muted-foreground">Low maintenance requirements</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <Check className="h-5 w-5 text-primary mt-0.5" />
                            <span className="text-sm text-muted-foreground">Eco-friendly and sustainable</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <Check className="h-5 w-5 text-primary mt-0.5" />
                            <span className="text-sm text-muted-foreground">Cost-effective solution</span>
                          </li>
                        </ul>
                      </div>
                      <div>
                        <h4 className="font-semibold mb-3">Ideal For:</h4>
                        <ul className="space-y-2">
                          <li className="flex items-start gap-2">
                            <Check className="h-5 w-5 text-primary mt-0.5" />
                            <span className="text-sm text-muted-foreground">Commercial buildings & offices</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <Check className="h-5 w-5 text-primary mt-0.5" />
                            <span className="text-sm text-muted-foreground">Retail & hospitality venues</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <Check className="h-5 w-5 text-primary mt-0.5" />
                            <span className="text-sm text-muted-foreground">Residential homes & villas</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <Check className="h-5 w-5 text-primary mt-0.5" />
                            <span className="text-sm text-muted-foreground">Warehouses & industrial spaces</span>
                          </li>
                        </ul>
                      </div>
                    </div>
                    <div className="flex justify-center">
                      <Link to={`/services/${service.slug}`}>
                        <Button 
                          className={getServiceButtonStyle(service.slug)}
                          size="lg"
                          data-testid={`service-read-more-${service.slug}`}
                        >
                          Learn More About {service.name} <ArrowRight className="ml-2 h-5 w-5" />
                        </Button>
                      </Link>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-spacing bg-secondary/30">
        <div className="container-custom text-center">
          <motion.div {...fadeInUp}>
            <h2 className="text-3xl font-bold mb-4">Ready to Get Started?</h2>
            <p className="text-muted-foreground mb-8 max-w-2xl mx-auto">
              Contact us today for a free consultation and quote
            </p>
            <Link to="/contact">
              <Button className="btn-primary" size="lg" data-testid="services-cta-btn">
                Request a Quote
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
